import { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import dayjs from "dayjs";
import { useHistory, useLocation } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import {
  DndContext, DragOverlay, closestCorners,
  MouseSensor, TouchSensor, useSensor, useSensors,
  useDroppable, useDraggable,
} from "@dnd-kit/core";
import { SettingsContext, UserContext, useEntitlement } from "../contexts/index.jsx";
import { useAlert } from "react-alert";
import { passedTime } from "../core/utils/time";
import { getFileUrl } from "../core/config/api.js";
import { canEditDealDate, canMoveTransaction, isDealDateEditable } from "../helpers/adminAccess";
import Avatar from "../components/Avatar.jsx";
import { Button, SearchInput, Segmented, StatePanel, Tabs } from "../components/ui";
import { useStoreWriteGuard } from "../helpers/storeScope";
import { PageHeader, PageLayout } from "../components/layout";
import ChangeSaleLeadModal from "../components/ChangeSaleLeadModal.jsx";
import ChangeDealDateModal from "../components/ChangeDealDateModal.jsx";
import MoveTransactionModal from "../components/MoveTransactionModal.jsx";
import { formatRequestId } from "../order/helpers";
import { boxLabels } from "../product/constants";
import { conditionFormatter } from "../core/utils/formatters";
import {
  formatMoney as formatMoneyBase,
  useGbpToBase,
} from "../core/currency";
import { useAdmins } from "../hooks/admins";
import { ShareHelper } from "../helpers";
import { useSendToWhatsApp, requestSubject } from "../waBroadcast";
import { storeCaption } from "../waBroadcast/waBroadcastApi";
import { requireResponse } from "../core/errors/requireResponse";
import {
  hasShareImage,
  prefetchShareImage,
  shareImage,
  shareImageLabel,
} from "../helpers/shareImage";
import Select from "react-select";
import "./KanbanRequests.scss";
import { HOME_PATH } from "../core/config/dashboard.js";

const COLUMNS = [
  { id: "new",         label: "New" },
  { id: "in_progress", label: "Send Proposal" },
  { id: "negotiation", label: "Negotiation" },
  { id: "follow_up",   label: "Follow-up" },
  { id: "deposited",   label: "Deposited" },
  { id: "done",        label: "Done" },
];

const STATUS_COLUMN_MAP = { listing_creation: "new", draft: "new" };

const COLUMN_STATUSES = {
  new:         ["new", "listing_creation", "draft"],
  in_progress: ["in_progress"],
  negotiation: ["negotiation"],
  follow_up:   ["follow_up"],
  deposited:   ["deposited"],
  done:        ["done"],
};

const PAGE_SIZE = 10;

// A column advertises another page only for the statuses that actually have
// one, so it can never offer a page load-more is unable to fetch.
function hasMoreFromPaging(colPaging) {
  return Object.values(colPaging || {}).some((cursor) => cursor?.hasMore);
}

// The live cursors and the cached snapshot must not alias each other, or paging
// past page one leaves the cache claiming rows its `items` never received.
function clonePaging(paging) {
  return Object.fromEntries(
    Object.entries(paging || {}).map(([colId, statuses]) => [
      colId,
      Object.fromEntries(Object.entries(statuses || {}).map(([status, cursor]) => [status, { ...cursor }])),
    ])
  );
}

const PERIOD_MAP = { month: "last_month", quarter: "last_three_month", year: "year" };
const PERIODS = [
  { key: "month",   label: "M" },
  { key: "quarter", label: "Q" },
  { key: "year",    label: "Y" },
];

// The board's view state lives in the URL, not in component state: `/kanban` is
// a top-level route, so walking to Orders (or any sidebar page) unmounts this
// view entirely. Held in state, a rep's search term and SELL tab were silently
// lost on the way back. Defaults are omitted from the query string so a clean
// `/kanban` stays clean and shareable.
const VIEW_DEFAULTS = { requestTab: "order", admin: "", search: "", period: "month", showStaleOnly: false };

function parseView(queryString) {
  const params = new URLSearchParams(queryString || "");
  const period = params.get("period");
  return {
    requestTab: params.get("tab") === "sell" ? "sell" : VIEW_DEFAULTS.requestTab,
    admin: params.get("user") || VIEW_DEFAULTS.admin,
    search: params.get("q") || VIEW_DEFAULTS.search,
    period: PERIOD_MAP[period] ? period : VIEW_DEFAULTS.period,
    showStaleOnly: params.get("stale") === "1",
  };
}

function serializeView(view) {
  const params = new URLSearchParams();
  if (view.requestTab !== VIEW_DEFAULTS.requestTab) params.set("tab", view.requestTab);
  if (view.admin) params.set("user", String(view.admin));
  if (view.search) params.set("q", view.search);
  if (view.period !== VIEW_DEFAULTS.period) params.set("period", view.period);
  if (view.showStaleOnly) params.set("stale", "1");
  const query = params.toString();
  return query ? `?${query}` : "";
}

// One board load is 8 POSTs (six columns, `new` fanned out to three statuses),
// so the per-filter cache is what keeps tab flips and returns cheap. It used to
// grow one entry per debounced keystroke and serve indefinitely stale cards:
// bound it by recency and by age instead.
// How long a cached board may be SHOWN for. Generous, because a hit is always
// revalidated in the background when it is older than STALE_AFTER_MS — the rep
// sees their cards instantly and the fresh ones swap in under a column spinner.
const CACHE_TTL_MS = 300000;
const STALE_AFTER_MS = 15000;
const CACHE_MAX_ENTRIES = 12;

function readCache(cache, key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  // A Map iterates in insertion order — re-inserting marks this key as the most
  // recently used one, so the eviction below always drops the coldest entry.
  cache.delete(key);
  cache.set(key, entry);
  return entry;
}

function writeCache(cache, key, entry) {
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > CACHE_MAX_ENTRIES) cache.delete(cache.keys().next().value);
}


// The board's cache lives at module scope, not in a ref, so it survives the
// component unmounting. /kanban is a top-level route: walking to Orders and
// back tears KanbanRequests down completely, and a per-mount cache meant that
// return trip always paid a full cold reload — eight requests and an empty
// board with spinners — even though the search and tab were preserved in the
// URL. The key carries the viewer and their active store, so nothing can be
// served across a user or a store switch.
const boardCache = new Map();

const scopedCacheKey = (userId, storeId, viewKey) =>
  `${userId ?? "anon"}|${storeId ?? "nostore"}|${viewKey}`;

// Shared mutable state needs an explicit way to be cleared: without this every
// test case after the first is served the previous one's board and issues no
// requests at all. Exported rather than reset on mount, because surviving the
// unmount is the entire point of the cache.
export const __resetBoardCache = () => boardCache.clear();
// ponytail: stays a literal, not var(--font-sans) — the M03 type-roles block
// resolves that token to Neue Haas, and this M53A slice is byte-identical
// only. Falls to the deliberate typography pass with the other leftovers.
const FILTER_FONT = "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif";

// Inline style applied directly to rendered DOM nodes — wins over all CSS/emotion.
const FS = {
  fontFamily: FILTER_FONT,
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const UFPlaceholder = ({ children, innerProps }) => (
  <div {...innerProps} style={{ ...FS, color: "#595959", gridArea: "1/1/2/3", overflow: "hidden", whiteSpace: "nowrap" }}>
    {children}
  </div>
);
const UFSingleValue = ({ children, innerProps }) => (
  <div {...innerProps} style={{ ...FS, color: "var(--rc-ui-text)", gridArea: "1/1/2/3", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
    {children}
  </div>
);
const UFOption = ({ children, innerProps, isSelected, isFocused }) => (
  <div {...innerProps} style={{ ...FS, color: isSelected ? "var(--rc-ui-text)" : "var(--rc-ui-text-soft)", background: isSelected ? "rgba(113,69,208,0.2)" : isFocused ? "var(--rc-ui-surface-2)" : "transparent", padding: "8px 12px", cursor: "pointer" }}>
    {children}
  </div>
);

const UF_COMPONENTS = { Placeholder: UFPlaceholder, SingleValue: UFSingleValue, Option: UFOption };

const UF_STYLES = {
  control: (base) => ({ ...base, minHeight: 36, height: 36, background: "var(--rc-ui-surface)", border: "1px solid #303030", borderRadius: 8, boxShadow: "none", cursor: "pointer" }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  input: (base) => ({ ...base, color: "var(--rc-ui-text)", margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 36 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#595959", padding: "0 8px" }),
  menu: (base) => ({ ...base, background: "var(--rc-ui-surface)", border: "1px solid var(--rc-ui-border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", overflow: "hidden" }),
  option: () => ({}),
  menuPortal: (base) => ({ ...base, zIndex: 9999999999999 }),
};

function KanbanUserFilter({ admin, setAdmin }) {
  const { admins } = useAdmins({ forOptions: true, allOption: true });
  const value = admin ? (admins?.find((o) => String(o.id) === String(admin)) ?? null) : null;
  return (
    <Select
      className="kanban-user-filter"
      classNamePrefix="select"
      placeholder="User"
      options={admins}
      value={value}
      getOptionLabel={(o) => o.name}
      getOptionValue={(o) => String(o.id)}
      onChange={(opt) => setAdmin(opt?.id ?? "")}
      isClearable={false}
      isSearchable
      maxMenuHeight={170}
      menuPortalTarget={document.body}
      components={UF_COMPONENTS}
      styles={UF_STYLES}
    />
  );
}

// Thin delegate to the shared currency module. ALWAYS pass the record's own
// currency (request budgets / price expectations / quote prices). Never call it
// without one for a GBP-pivot figure — that labels a GBP number with the base
// symbol, which is the bug `pivot` below exists to prevent.
function formatMoney(amount, currency) {
  if (!amount) return "–";
  return formatMoneyBase(amount, { currency, decimals: 0 });
}

// Card money that is a GBP-PIVOT AGGREGATE, rendered in the store base.
//
// INVARIANT: these figures have no recoverable original currency. card_costs /
// card_profit are the backend's costs_gbp / profit_gbp (RequestService::
// listingCardFinancials sums the listing's gbp_* columns), the DONE banner is a
// server-side Σ profit_gbp over every done deal, and the quote fallbacks mirror
// the same GBP formulas. Each is already a sum across records that may each
// have been entered in a different currency, so the per-record amount+currency
// pair resolveOriginalMoney() needs no longer exists by the time it reaches us.
// Converting the pivot is therefore correct here — unlike the per-listing cost
// rows on the order page, which must use the original.
//
// `pivot` is a useGbpToBase() handle, taken by the component that renders.
function formatPivot(pivot, amount) {
  if (!amount) return "–";
  return pivot.format(amount, { decimals: 0 });
}

function getBudgetTier(amount) {
  if (!amount) return "";
  const gbp = amount / 100;
  if (gbp >= 50000) return " kanban-card__value--tier-gold";
  if (gbp >= 10000) return " kanban-card__value--tier-blue";
  return "";
}

function lastMoveLabel(dateStr) {
  if (!dateStr) return null;
  const hours = dayjs().diff(dayjs(dateStr), "hour");
  if (hours < 24) return "today";
  return `${Math.floor(hours / 24)}d ago`;
}

function ageLabel(dateStr) {
  if (!dateStr) return null;
  const days = dayjs().diff(dayjs(dateStr), "day");
  if (days === 0) return "today";
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

const QUOTE_PENDING_COLS = new Set(["new"]);
const INACTIVE_COLS      = new Set(["negotiation", "follow_up"]);

function hasSpeedBadge(item) {
  if (!item.quote_approved_at) return false;
  const minutes = dayjs(item.quote_approved_at).diff(dayjs(item.created_at), "minute");
  const clientAgeHours = dayjs(item.created_at).diff(dayjs(item.client?.created_at), "hour");
  const isNewClient = clientAgeHours < 24;
  return isNewClient ? minutes <= 240 : minutes <= 60;
}

function getStaleType(item) {
  const col = getItemColumn(item);
  if (QUOTE_PENDING_COLS.has(col) && !item.quote_approved_at && dayjs().diff(dayjs(item.created_at), "hour") >= 24)
    return "no_quote";
  if (INACTIVE_COLS.has(col) && dayjs().diff(dayjs(item.updated_at), "day") >= 5)
    return "inactive";
  return null;
}

function getQuoteGlow(item) {
  if (getItemColumn(item) !== "new") return null;
  const quotes = item.quotes || [];
  if (quotes.some((q) => q.status === "approved")) return "approved";
  if (quotes.some((q) => q.status === "to_review")) return "to_review";
  return null;
}

function sumCosts(costs) {
  if (!costs) return 0;
  return (Number(costs.polishing) || 0)
    + (Number(costs.accessories) || 0)
    + (Number(costs.shipping) || 0)
    + (Number(costs.travel_fees) || 0);
}

// Gross profit (Selling − Buying, handling costs NOT subtracted) from a quote.
// The fallback when the backend hasn't attached a listing-sourced card_profit
// (pre-order pipeline cards). Matches the DONE banner's gross definition so the
// cards and banner reconcile.
function calcOrderGrossProfit(quote) {
  if (!quote?.price) return null;
  let buying = null;
  if (quote.listing_gbp_buying_price) {
    buying = quote.listing_gbp_buying_price;
  } else if (quote.product_price?.gbp_price) {
    const fx = quote.counter_fx_price || 1;
    buying = quote.product_price.gbp_price * fx;
  }
  if (buying === null) return null;
  return quote.price - buying;
}

// The card's Profit/Costs prefer the backend's listing-sourced figures
// (card_profit / card_costs) — accurate for orders whose costs+buying live on
// the listing, and split per-watch for grouped multi-orders. They fall back to
// the quote estimate when the request has no listing yet. `?? ` keeps a real 0.
function cardOrderProfit(item) {
  return item.card_profit ?? calcOrderGrossProfit(item.last_quote);
}
function cardOrderCosts(item) {
  return item.card_costs ?? sumCosts(item.last_quote?.costs);
}

// True when the card can't show Profit purely because the buying price is
// missing (the transacted listing has a sale price but no buying). The card
// then shows an "Add Buying Price" prompt instead of "–"; gross profit renders
// automatically once the buying price is entered.
function cardNeedsBuyingPrice(item) {
  return cardOrderProfit(item) === null && !!item.card_needs_buying_price;
}

function calcSellStraight(quote) {
  if (!quote?.price || (!quote?.trade_avg && !quote?.market_avg)) return null;
  const fx = quote.counter_fx_price || 1;
  const costs = sumCosts(quote.costs);
  return {
    min: quote.trade_avg  ? (quote.trade_avg  * fx) - quote.price - costs : null,
    max: quote.market_avg ? (quote.market_avg * fx) - quote.price - costs : null,
  };
}

function calcSellConsignment(quote) {
  if (!quote?.min_price || !quote?.max_price) return null;
  if (!quote?.trade_avg && !quote?.market_avg) return null;
  const fx = quote.counter_fx_price || 1;
  const costs = sumCosts(quote.costs);
  return {
    min: quote.trade_avg  ? (quote.trade_avg  * fx) - quote.max_price - costs : null,
    max: quote.market_avg ? (quote.market_avg * fx) - quote.min_price - costs : null,
  };
}

function profitClass(val) {
  if (val === null || val === undefined) return "";
  return val >= 0 ? " kanban-card__profit--positive" : " kanban-card__profit--negative";
}

function formatProfitSingle(val, pivot) {
  if (val === null || val === undefined) return { text: "–", cls: "" };
  return { text: formatPivot(pivot, val), cls: profitClass(val) };
}

function formatProfitRange(range, pivot) {
  if (!range || (range.min === null && range.max === null)) return { text: "–", cls: "" };
  if (range.min !== null && range.max !== null) {
    const cls = range.max < 0 ? " kanban-card__profit--negative"
      : range.min < 0 ? " kanban-card__profit--mixed"
      : " kanban-card__profit--positive";
    return {
      text: `${formatPivot(pivot, range.min)} – ${formatPivot(pivot, range.max)}`,
      cls,
    };
  }
  const val = range.min ?? range.max;
  return formatProfitSingle(val, pivot);
}

function formatDoneProfit(items, isSell, pivot) {
  let total = 0;
  let hasData = false;
  for (const item of items) {
    if (isSell) {
      const q = item.last_quote;
      if (!q) continue;
      const s = calcSellStraight(q);
      if (s && s.min !== null && s.max !== null) {
        total += (s.min + s.max) / 2;
        hasData = true;
      }
    } else {
      const p = cardOrderProfit(item);
      if (p !== null) { total += p; hasData = true; }
    }
  }
  if (!hasData) return null;
  // Convert the GBP-pivot Σ into the base BEFORE compacting, so the k/m
  // magnitude is the base magnitude and not a GBP one wearing a base symbol.
  const amount = (pivot.toBase(total) ?? total) / 100;
  const sym = pivot.symbol;
  if (Math.abs(amount) >= 1000000) return `${sym}${(amount / 1000000).toFixed(1)}m`;
  if (Math.abs(amount) >= 1000) return `${sym}${Math.round(Math.abs(amount) / 1000)}k`;
  return `${sym}${Math.round(amount)}`;
}

// Format a pence integer to the compact £k/£m banner string. Used for the
// server-provided gross-profit aggregate (Σ over ALL done deals), which
// replaces the old client-side per-page sum. Unlike formatDoneProfit it
// preserves the sign (the legacy formatter dropped it via Math.abs).
function formatProfitK(pence, pivot) {
  if (pence === null || pence === undefined) return null;
  // Σ profit_gbp from the server — converted into the base before compacting.
  const amount = (pivot.toBase(pence) ?? pence) / 100;
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  const sym = pivot.symbol;
  if (abs >= 1000000) return `${sign}${sym}${(abs / 1000000).toFixed(1)}m`;
  if (abs >= 1000) return `${sign}${sym}${Math.round(abs / 1000)}k`;
  return `${sign}${sym}${Math.round(abs)}`;
}

function getItemColumn(item) {
  return STATUS_COLUMN_MAP[item.status] || item.status;
}

// Breakpoints aligned with KanbanRequests.scss:
//   sm  < 768   — stacked columns, swipeable compact cards (Notion mobile)
//   md  768–1199 — horizontal board, wrapped toolbar, condensed cards
//   lg  ≥ 1200  — full desktop board
const BP_SM_MAX = 767;
const BP_MD_MAX = 1199;

function getKanbanBp(width) {
  if (width <= BP_SM_MAX) return "sm";
  if (width <= BP_MD_MAX) return "md";
  return "lg";
}

function useKanbanLayout() {
  const [bp, setBp] = useState(() => getKanbanBp(window.innerWidth));
  useEffect(() => {
    const mqSm = window.matchMedia(`(max-width: ${BP_SM_MAX}px)`);
    const mqMd = window.matchMedia(`(max-width: ${BP_MD_MAX}px)`);
    const sync = () => setBp(getKanbanBp(window.innerWidth));
    sync();
    mqSm.addEventListener("change", sync);
    mqMd.addEventListener("change", sync);
    window.addEventListener("resize", sync);
    return () => {
      mqSm.removeEventListener("change", sync);
      mqMd.removeEventListener("change", sync);
      window.removeEventListener("resize", sync);
    };
  }, []);
  const isMobile = bp === "sm";
  const isTablet = bp === "md";
  return {
    bp,
    isMobile,
    isTablet,
    isDesktop: bp === "lg",
    // Notion mobile: compact swipeable cards + stacked columns
    stackedColumns: isMobile,
    compactCards: isMobile,
  };
}

// Horizontal "⋯" menu on each card, mirroring the Orders-tab AdminActionDropDown
// (Share to Broadcast / Send Proposal / Cancel & Hide). Rendered into a body
// portal so the dropdown escapes the card's `overflow: hidden` and the column's
// scroll container — it floats on top of the other cards.
const MENU_WIDTH = 188;
// Height is ESTIMATED from the item count rather than fixed: it drives both the
// flip-upward decision and the upward `top`, so a menu that renders taller than
// the estimate opens over its own trigger. Two of the rows are conditional
// (Share to Broadcast, Change Deal Date), so a single constant is wrong for
// three of the four combinations. 33px per row (8+8 padding + ~17 line box) plus
// the menu's own 4+4 padding and 1+1 border.
const MENU_ITEM_HEIGHT = 33;
const MENU_CHROME = 10;
const menuHeight = (rows) => rows * MENU_ITEM_HEIGHT + MENU_CHROME;

function KanbanCardMenu({ item, isShopify, onAction }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  // "Share via WhatsApp" (key share_to_broadcast) — the Send via WhatsApp row.
  const canShareBroadcast = useEntitlement("share_to_broadcast");
  // Read from context here rather than threaded down as a prop: the menu is
  // rendered from inside CardContent, three layers below the board, and neither
  // of the layers between has any other use for the user.
  const { user } = useContext(UserContext) || {};
  const canChangeDate = canEditDealDate(user) && isDealDateEditable(item);
  const canMove = canMoveTransaction(user);
  // Copy/Save Image — the photo half of Share to Broadcast, so it follows the
  // same plan switch and additionally needs the request to have a photo.
  const showBroadcast = !isShopify && canShareBroadcast;
  const showShareImage = showBroadcast && hasShareImage(item);
  // "Share to Broadcast" — the WhatsApp Broadcast row (wa_shared_number).
  const wa = useSendToWhatsApp();
  const showWa = wa.canSend(item);

  const close = useCallback(() => { setOpen(false); setPos(null); }, []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    const dismiss = () => close();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick, { passive: true });
    window.addEventListener("resize", dismiss);
    // capture so a scroll on any ancestor (e.g. the column body) also dismisses
    window.addEventListener("scroll", dismiss, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [open, close]);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) { close(); return; }
    // Warm the photo now rather than on the item click: Safari treats the tap's
    // user activation as spent across an await, so fetching inside the click
    // handler would make the clipboard write throw NotAllowedError.
    if (showShareImage) prefetchShareImage(item);
    const rect = triggerRef.current.getBoundingClientRect();
    // Send Proposal + Change Sale Lead + Cancel & Hide are always rendered.
    // Must mirror the render below EXACTLY: the broadcast row is gated on BOTH
    // !isShopify and the share_to_broadcast entitlement, so counting it from
    // isShopify alone would over-estimate the height for a store without the
    // feature and open the menu misaligned.
    const rows =
      3 +
      (showBroadcast ? 1 : 0) +
      // Must mirror the render below EXACTLY (see the note above): Copy/Save
      // Image is gated on the broadcast row AND the request having a photo.
      (showShareImage ? 1 : 0) +
      (showWa ? 1 : 0) +
      (canChangeDate ? 1 : 0) +
      // Must mirror the render below EXACTLY (see the note above): this count
      // drives both the flip-upward decision and the upward `top`, so omitting
      // the Move Transaction row would open the menu over its own trigger.
      (canMove ? 1 : 0);
    const height = menuHeight(rows);
    const openUp = window.innerHeight - rect.bottom < height + 16;
    setPos({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8)),
      top: openUp ? rect.top - height - 6 : rect.bottom + 6,
    });
    setOpen(true);
  };

  const handle = (e, action) => {
    e.stopPropagation();
    close();
    onAction(action);
  };

  // Stop pointer/click from reaching the card's drag listeners and navigation.
  const stop = (e) => e.stopPropagation();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="kanban-card__menu-trigger"
        aria-label="Card actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        onPointerDown={stop}
      >
        <span className="kanban-card__menu-dot" />
        <span className="kanban-card__menu-dot" />
        <span className="kanban-card__menu-dot" />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="kanban-card-menu"
          role="menu"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_WIDTH }}
          onClick={stop}
          onPointerDown={stop}
        >
          {/* "Share via WhatsApp" (share_to_broadcast) — follows the plan switch. */}
          {showBroadcast && (
            <button type="button" role="menuitem" className="kanban-card-menu__item" onClick={(e) => handle(e, "broadcast")}>
              Send via WhatsApp
            </button>
          )}
          {/* The photo, which the text-only WhatsApp deeplink above cannot carry.
              The label reflects what this browser can do — see helpers/shareImage. */}
          {showShareImage && (
            <button type="button" role="menuitem" className="kanban-card-menu__item" onClick={(e) => handle(e, "copy_image")}>
              {shareImageLabel()}
            </button>
          )}
          {showWa && (
            <button
              type="button"
              role="menuitem"
              className="kanban-card-menu__item"
              onClick={(e) => { e.stopPropagation(); close(); wa.open(requestSubject(item)); }}
            >
              Share to Broadcast
            </button>
          )}
          <button type="button" role="menuitem" className="kanban-card-menu__item" onClick={(e) => handle(e, "in_progress")}>
            Send Proposal
          </button>
          <button type="button" role="menuitem" className="kanban-card-menu__item" onClick={(e) => handle(e, "change_lead")}>
            Change Sale Lead
          </button>
          {canChangeDate && (
            <button type="button" role="menuitem" className="kanban-card-menu__item" onClick={(e) => handle(e, "change_date")}>
              Change Deal Date
            </button>
          )}
          {canMove && (
            <button type="button" role="menuitem" className="kanban-card-menu__item" onClick={(e) => handle(e, "move_transaction")}>
              Move Transaction
            </button>
          )}
          <button type="button" role="menuitem" className="kanban-card-menu__item kanban-card-menu__item--danger" onClick={(e) => handle(e, "canceled")}>
            Cancel &amp; Hide
          </button>
        </div>,
        document.body
      )}
      {/* The sheet portals to <body>, but React still bubbles its clicks through
          here — stop them before they reach the card's drag and open handlers. */}
      {/* Mouse/touch too: the card drags on MouseSensor (mousedown) and
          TouchSensor (touchstart) — selecting caption text in the sheet would
          otherwise drag the request behind it into another column. */}
      {wa.sheet && (
        <span onClick={stop} onPointerDown={stop} onMouseDown={stop} onTouchStart={stop}>{wa.sheet}</span>
      )}
    </>
  );
}

function CardContent({ item, settings, staleType, onCardAction, compact = false }) {
  // GBP pivot → store base for the Costs / Profit / Straight / Consign. rows.
  const pivot = useGbpToBase();
  const timer = passedTime(item.created_at, dayjs(item.quote_approved_at || undefined));
  const isSell = item.request_type === "sell";
  const clientName = !item.client?.is_admin
    ? `${item.client?.first_name || ""} ${item.client?.last_name || ""}`.trim() || null
    : null;
  const admin = item.created_by?.is_admin ? item.created_by : null;
  // #6: ORDER cards show one avatar per commission recipient (commission_users
  // from the API). Sell cards have no commission logic, so they keep the single
  // created_by avatar. Fall back to created_by when an order has no commissions.
  const commissionUsers = !isSell && Array.isArray(item.commission_users) ? item.commission_users : [];
  const avatarUsers = commissionUsers.length > 0
    ? commissionUsers
    : (admin ? [admin] : []);
  const requestId = settings?.settings ? formatRequestId(settings.settings, item) : null;
  const waPhone = item.client?.phone ? item.client.phone.replace(/\D/g, "") : null;
  const budgetTier = isSell ? getBudgetTier(item.price_expectation) : getBudgetTier(item.max_budget);
  const speedBadge = hasSpeedBadge(item);

  if (compact) {
    const { text: profitText, cls: profitCls } = !isSell
      ? formatProfitSingle(cardOrderProfit(item), pivot)
      : formatProfitRange(calcSellStraight(item.last_quote), pivot);
    const needsBuying = !isSell && cardNeedsBuyingPrice(item);
    // Budgets / price expectation render in the request's own currency.
    const budgetText = isSell
      ? (item.price_expectation ? formatMoney(item.price_expectation, item.currency) : null)
      : (item.min_budget || item.max_budget ? `${formatMoney(item.min_budget, item.currency)} – ${formatMoney(item.max_budget, item.currency)}` : null);

    return (
      <>
        <div className="kanban-card__badges kanban-card__badges--compact">
          {speedBadge && <span className="kanban-card__speed-star" title="Quoted fast">★</span>}
          <span className={`kanban-badge kanban-badge--${isSell ? "sell" : "order"}`}>{isSell ? "SELL" : "ORDER"}</span>
        </div>
        <div className="kanban-card__compact">
          <img className="kanban-card__img kanban-card__img--compact" src={getFileUrl(item?.product?.images?.[0]?.id)} alt="" />
          <div className="kanban-card__compact-body">
            <div className="kanban-card__compact-top">
              <span className="kanban-card__brand">{item?.product?.brand?.name}</span>
              <div className="kanban-card__model">{item?.product?.model?.name}</div>
              {item.product?.reference_number && (
                <div className="kanban-card__ref">{item.product.reference_number}</div>
              )}
            </div>
            <div className="kanban-card__compact-meta kanban-card__compact-meta--client">
              <span className="kanban-card__client-name">{clientName || "–"}</span>
              {waPhone && (
                <>
                  <span className="kanban-card__compact-sep" aria-hidden>|</span>
                  <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer" className="kanban-card__wa-link" onClick={(e) => e.stopPropagation()}>
                    {item.client.phone}
                  </a>
                </>
              )}
            </div>
            <div className="kanban-card__compact-meta kanban-card__compact-meta--id">
              {budgetText && (
                <span className={`kanban-card__budget-inline${budgetTier}`}>{budgetText}</span>
              )}
              {requestId && <span className="kanban-card__id">{requestId}</span>}
            </div>
            {!isSell && (
              <div className="kanban-card__compact-meta kanban-card__compact-meta--profit">
                {needsBuying
                  ? <span className="kanban-card__missing-pill">Add Buying Price</span>
                  : <span className={`kanban-card__profit${profitCls}`}>Profit {profitText}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="kanban-card__timer-row kanban-card__timer-row--compact">
          {onCardAction ? (
            <KanbanCardMenu
              item={item}
              isShopify={!!settings?.isShopify}
              onAction={(action) => onCardAction(item, action)}
            />
          ) : <span />}
          <div className="kanban-card__timer-right">
            {staleType === "no_quote" ? (
              <span className="kanban-card__last-move kanban-card__last-move--stale">⚠ No quote</span>
            ) : staleType === "inactive" ? (
              <span className="kanban-card__last-move kanban-card__last-move--stale">⚠ {lastMoveLabel(item.updated_at)}</span>
            ) : lastMoveLabel(item.updated_at) ? (
              <span className="kanban-card__last-move kanban-card__last-move--active">↻ {lastMoveLabel(item.updated_at)}</span>
            ) : null}
            {avatarUsers.length > 0 && (
              <div className="kanban-card__admin-avatars">
                {avatarUsers.map((u, i) => {
                  const label = u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim();
                  return (
                    <div key={u.id || i} className="kanban-card__admin-avatar" title={label}>
                      <Avatar user={u} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {item.quoted_price && (
          <div className="kanban-card__quote-badge">Quote · {formatMoney(item.quoted_price, item.last_quote?.currency ?? item.quoted_currency)}</div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Badges — absolutely positioned top-right, outside text flow */}
      <div className="kanban-card__badges">
        {speedBadge && <span className="kanban-card__speed-star" title="Quoted fast">★</span>}
        <span className={`kanban-badge kanban-badge--${isSell ? "sell" : "order"}`}>{isSell ? "SELL" : "ORDER"}</span>
      </div>

      {/* Header: image left, info right */}
      <div className="kanban-card__header">
        <img className="kanban-card__img" src={getFileUrl(item?.product?.images?.[0]?.id)} alt="" />
        <div className="kanban-card__title">
          <div className="kanban-card__title-identity">
            <span className="kanban-card__brand">{item?.product?.brand?.name}</span>
            <div className="kanban-card__model">{item?.product?.model?.name}</div>
            {item.product?.reference_number && (
              <div className="kanban-card__ref">{item.product.reference_number}</div>
            )}
          </div>
          <div className="kanban-card__title-meta">
            {item.year && <span className="kanban-card__attr">{item.year}{!isSell && " +"}</span>}
            {item.condition?.length > 0 && <span className="kanban-card__attr">{conditionFormatter(item.condition)}</span>}
            <span className={`kanban-card__budget-inline${budgetTier}`}>
              {/* Budgets / price expectation in the request's own currency. */}
              {isSell
                ? (item.price_expectation ? formatMoney(item.price_expectation, item.currency) : "–")
                : (item.min_budget || item.max_budget ? `${formatMoney(item.min_budget, item.currency)} – ${formatMoney(item.max_budget, item.currency)}` : "–")}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="kanban-card__footer">
        <div className="kanban-card__divider" />
        <div className="kanban-card__row"><span className="kanban-card__label">{isSell ? "Seller" : "Client"}</span><span className="kanban-card__value">{clientName || "–"}</span></div>
        <div className="kanban-card__row">
          <span className="kanban-card__label">Phone</span>
          <span className="kanban-card__value">
            {waPhone ? (
              <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer" className="kanban-card__wa-link" onClick={e => e.stopPropagation()}>
                <svg className="kanban-card__wa-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {item.client.phone}
              </a>
            ) : "–"}
          </span>
        </div>
        {!isSell && (() => {
          const { text, cls } = formatProfitSingle(cardOrderProfit(item), pivot);
          const costs = cardOrderCosts(item);
          const needsBuying = cardNeedsBuyingPrice(item);
          return <>
            <div className="kanban-card__row"><span className="kanban-card__label">Costs</span><span className="kanban-card__value">{costs > 0 ? formatPivot(pivot, costs) : "–"}</span></div>
            <div className="kanban-card__row"><span className="kanban-card__label">Profit</span>
              {needsBuying
                ? <span className="kanban-card__value"><span className="kanban-card__missing-pill">Add Buying Price</span></span>
                : <span className={`kanban-card__value kanban-card__profit${cls}`}>{text}</span>}
            </div>
          </>;
        })()}
        {isSell && (() => {
          const straight = formatProfitRange(calcSellStraight(item.last_quote), pivot);
          const consign  = formatProfitRange(calcSellConsignment(item.last_quote), pivot);
          const costs = sumCosts(item.last_quote?.costs);
          return <>
            <div className="kanban-card__row"><span className="kanban-card__label">Costs</span><span className="kanban-card__value">{costs > 0 ? formatPivot(pivot, costs) : "–"}</span></div>
            <div className="kanban-card__row"><span className="kanban-card__label">Straight</span><span className={`kanban-card__value kanban-card__profit${straight.cls}`}>{straight.text}</span></div>
            <div className="kanban-card__row"><span className="kanban-card__label">Consign.</span><span className={`kanban-card__value kanban-card__profit${consign.cls}`}>{consign.text}</span></div>
          </>;
        })()}
        <div className="kanban-card__row"><span className="kanban-card__label">ID</span><span className="kanban-card__value kanban-card__id">{requestId || "–"}<span className="kanban-card__sep"> | </span>{dayjs(item.created_at).format("DD/MM/YYYY")}</span></div>
        <div className="kanban-card__row"><span className="kanban-card__label">Channel</span><span className="kanban-card__value kanban-card__id">{item.channel || "–"}</span></div>
        {item.quoted_price && (
          <div className="kanban-card__quote-badge">Quote · {formatMoney(item.quoted_price, item.last_quote?.currency ?? item.quoted_currency)}</div>
        )}
        <div className="kanban-card__timer-row">
          {onCardAction ? (
            <KanbanCardMenu
              item={item}
              isShopify={!!settings?.isShopify}
              onAction={(action) => onCardAction(item, action)}
            />
          ) : <span />}
          <div className="kanban-card__timer-right">
            {staleType === "no_quote" ? (
              <span className="kanban-card__last-move kanban-card__last-move--stale">⚠ No quote</span>
            ) : staleType === "inactive" ? (
              <span className="kanban-card__last-move kanban-card__last-move--stale">⚠ {lastMoveLabel(item.updated_at)}</span>
            ) : lastMoveLabel(item.updated_at) ? (
              <span className="kanban-card__last-move kanban-card__last-move--active">↻ {lastMoveLabel(item.updated_at)}</span>
            ) : null}
            {avatarUsers.length > 0 && (
              <div className="kanban-card__admin-avatars">
                {avatarUsers.map((u, i) => {
                  const label = u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim();
                  return (
                    <div key={u.id || i} className="kanban-card__admin-avatar" title={label}>
                      <Avatar user={u} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function KanbanCard({ item, settings, selectMode, isSelected, onToggleSelect, onCardAction, compact = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(item.id),
    disabled: selectMode,
  });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const handleClick = (e) => {
    if (selectMode) {
      e.stopPropagation();
      onToggleSelect(item.id);
      return;
    }
    if (!transform) {
      // PX-linked cards (the order request and its trade-in sell requests both
      // carry px_request_id) must open the unified px-order view, not the
      // standalone /request/sell|order route. Mirrors SellItem/AdminActions.
      const typePath = item?.px_request_id ? "px-order" : item.request_type;
      const id = item?.px_request_id || item.id;
      window.open(`/request/${typePath}/${id}`, "_blank");
    }
  };
  // In select mode, don't spread drag listeners/attributes — clicks toggle selection instead
  const staleType = getStaleType(item);
  const quoteGlow = getQuoteGlow(item);
  const dragProps = selectMode ? {} : { ...attributes, ...listeners };
  const className = [
    "kanban-card",
    compact && "kanban-card--compact",
    isDragging && "kanban-card--dragging",
    quoteGlow === "approved" && "kanban-card--glow-approved",
    quoteGlow === "to_review" && "kanban-card--glow-to-review",
    !quoteGlow && staleType && "kanban-card--stale",
    selectMode && "kanban-card--selectable",
    isSelected && "kanban-card--selected",
  ].filter(Boolean).join(" ");
  return (
    <div ref={setNodeRef} style={style} className={className} onClick={handleClick} {...dragProps}>
      {selectMode && (
        <div className="kanban-card__select">
          <span className={`kanban-card__checkbox${isSelected ? " kanban-card__checkbox--checked" : ""}`} aria-hidden>
            {isSelected && "✓"}
          </span>
        </div>
      )}
      <CardContent item={item} settings={settings} staleType={quoteGlow ? null : staleType} onCardAction={selectMode ? undefined : onCardAction} compact={compact} />
    </div>
  );
}

const MAX_CARD_DOTS = 12;

const ICarouselPrev = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ICarouselNext = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

function CardCarouselDots({ count, activeIndex, onDotClick }) {
  if (count <= 0) return null;
  const visible = Math.min(count, MAX_CARD_DOTS);
  // When capped, map the active index into the visible window proportionally.
  const mappedActive = count > visible
    ? Math.min(visible - 1, Math.round((activeIndex / Math.max(count - 1, 1)) * (visible - 1)))
    : activeIndex;
  return (
    <div className="kanban-card-dots" aria-label={`${count} card${count === 1 ? "" : "s"}`}>
      {Array.from({ length: visible }).map((_, i) => (
        <button
          key={i}
          type="button"
          className={`kanban-card-dot${i === mappedActive ? " kanban-card-dot--active" : ""}`}
          onClick={() => {
            if (!onDotClick) return;
            if (count <= visible) onDotClick(i);
            else onDotClick(Math.round((i / Math.max(visible - 1, 1)) * (count - 1)));
          }}
          aria-label={`Card ${i + 1} of ${count}`}
        />
      ))}
    </div>
  );
}

function getCarouselStep(el) {
  if (!el) return 0;
  const card = el.querySelector(".kanban-card, .kanban-column__empty-card");
  if (!card) return el.clientWidth;
  const styles = window.getComputedStyle(el);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;
  return card.offsetWidth + gap;
}

function KanbanColumn({ column, items, total, isSell, settings, hasMore, isLoadingMore, onLoadMore, selectMode, selectedIds, onToggleSelect, onCardAction, serverProfit, period, layout }) {
  const { stackedColumns, compactCards, bp } = layout;
  // DONE-column total gross profit: a server-side Σ profit_gbp, so the same
  // GBP pivot → base conversion the cards use.
  const pivot = useGbpToBase();
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: column.id });
  const bodyRef = useRef(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  useEffect(() => {
    setActiveCardIndex(0);
    if (bodyRef.current) bodyRef.current.scrollLeft = 0;
  }, [column.id, items.length, stackedColumns]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onScroll = () => {
      if (stackedColumns && items.length > 0) {
        const step = getCarouselStep(el);
        if (step > 0) {
          setActiveCardIndex(Math.max(0, Math.min(Math.round(el.scrollLeft / step), items.length - 1)));
        }
      }
      if (!hasMore || isLoadingMore) return;
      if (stackedColumns) {
        if (el.scrollWidth - el.scrollLeft - el.clientWidth < 120) onLoadMore();
      } else if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
        onLoadMore();
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, isLoadingMore, onLoadMore, stackedColumns, items.length]);

  const scrollToCard = useCallback((idx) => {
    const el = bodyRef.current;
    if (!el) return;
    const step = getCarouselStep(el);
    const maxIdx = Math.max(0, items.length - 1);
    const next = Math.max(0, Math.min(idx, maxIdx));
    el.scrollTo({ left: next * step, behavior: "smooth" });
    setActiveCardIndex(next);
  }, [items.length]);

  const goPrev = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    scrollToCard(activeCardIndex - 1);
  }, [activeCardIndex, scrollToCard]);

  const goNext = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    scrollToCard(activeCardIndex + 1);
  }, [activeCardIndex, scrollToCard]);

  const setRefs = useCallback((node) => { bodyRef.current = node; setDropRef(node); }, [setDropRef]);
  const displayCount = total != null ? total : items.length;
  const isDone = column.id === "done";
  const doneProfit = isDone
    ? (!isSell && serverProfit !== null && serverProfit !== undefined
        ? formatProfitK(serverProfit, pivot)
        : formatDoneProfit(items, isSell, pivot))
    : null;
  const showEmptyPlaceholder = items.length === 0 && !isLoadingMore;
  const dotCount = showEmptyPlaceholder ? 1 : items.length;
  const canPrev = stackedColumns && items.length > 1 && activeCardIndex > 0;
  const canNext = stackedColumns && items.length > 1 && activeCardIndex < items.length - 1;

  return (
    <div className={`kanban-column kanban-column--${column.id} kanban-column--bp-${bp}${isOver ? " kanban-column--over" : ""}${stackedColumns ? " kanban-column--stacked" : ""}`}>
      <div className="kanban-column__header">
        <span className="kanban-column__label">{column.label}</span>
        <div className="kanban-column__header-right">
          <span className="kanban-column__count">{displayCount}</span>
        </div>
      </div>
      {isDone && doneProfit && (
        <div className={`kanban-column__profit-banner${isLoadingMore ? " kanban-column__profit-banner--refreshing" : ""}`}>
          <span className="kanban-column__profit-label">Profit</span>
          <span className="kanban-column__profit-value">{doneProfit}</span>
        </div>
      )}
      {stackedColumns ? (
        <div className="kanban-column__carousel">
          {canPrev && (
            <button
              type="button"
              className="kanban-carousel-nav kanban-carousel-nav--prev"
              onClick={goPrev}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Previous card"
            >
              <ICarouselPrev />
            </button>
          )}
          <div ref={setRefs} className="kanban-column__body kanban-column__body--carousel">
            {items.map((item) => (
              <KanbanCard
                key={item.id}
                item={item}
                settings={settings}
                selectMode={selectMode}
                isSelected={selectedIds?.has(item.id)}
                onToggleSelect={onToggleSelect}
                onCardAction={onCardAction}
                compact={compactCards}
              />
            ))}
            {showEmptyPlaceholder && (
              <div className="kanban-column__empty-card">No requests</div>
            )}
            {isLoadingMore && <div className="kanban-column__loading"><div className="kanban-column__loading-spinner" /></div>}
          </div>
          {canNext && (
            <button
              type="button"
              className="kanban-carousel-nav kanban-carousel-nav--next"
              onClick={goNext}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Next card"
            >
              <ICarouselNext />
            </button>
          )}
        </div>
      ) : (
        <div ref={setRefs} className="kanban-column__body">
          {items.map((item) => (
            <KanbanCard
              key={item.id}
              item={item}
              settings={settings}
              selectMode={selectMode}
              isSelected={selectedIds?.has(item.id)}
              onToggleSelect={onToggleSelect}
              onCardAction={onCardAction}
              compact={compactCards}
            />
          ))}
          {showEmptyPlaceholder && <div className="kanban-column__empty">No requests</div>}
          {isLoadingMore && <div className="kanban-column__loading"><div className="kanban-column__loading-spinner" /></div>}
        </div>
      )}
      {stackedColumns && (
        <CardCarouselDots
          count={dotCount}
          activeIndex={showEmptyPlaceholder ? 0 : activeCardIndex}
          onDotClick={items.length > 1 ? scrollToCard : undefined}
        />
      )}
    </div>
  );
}

const KanbanRequests = ({ user }) => {
  const alert = useAlert();
  const settings = useContext(SettingsContext);
  const { activeStoreId } = useContext(UserContext) || {};
  const layout = useKanbanLayout();
  const { isMobile, bp } = layout;
  const history = useHistory();
  const location = useLocation();

  // The URL is the single source of truth for the filter bar — no mirrored
  // state, so the browser back button can't desync the board from its query.
  const { requestTab, admin, search, period, showStaleOnly } = useMemo(
    () => parseView(location.search),
    [location.search]
  );

  const setView = useCallback((patch, push = false) => {
    const next = serializeView({ ...parseView(location.search), ...patch });
    if (next === (location.search || "")) return;
    const to = { pathname: location.pathname, search: next };
    // `replace` for filter edits — typing a reference would otherwise push one
    // history entry per debounced keystroke and bury the previous page. The
    // ORDER/SELL flip is a view the user may well want to step back out of.
    if (push) history.push(to);
    else history.replace(to);
  }, [history, location.pathname, location.search]);

  const setAdmin  = useCallback((value) => setView({ admin: value ?? "" }), [setView]);
  const setSearch = useCallback((value) => setView({ search: value }), [setView]);
  const setPeriod = useCallback((value) => setView({ period: value }), [setView]);
  const toggleStaleOnly = useCallback(
    () => setView({ showStaleOnly: !showStaleOnly }),
    [setView, showStaleOnly]
  );

  const isDefaultView = !admin && !search && period === VIEW_DEFAULTS.period && !showStaleOnly;
  // Same Master Store write guard as the Orders page's "+ Sourcing Enquiry".
  const { guardProps: storeGuard } = useStoreWriteGuard();

  const [items, setItems]                   = useState([]);
  const [columnHasMore, setColumnHasMore]   = useState({});
  const [columnLoading, setColumnLoading]   = useState({});
  const [columnTotal, setColumnTotal]       = useState({});
  const [columnProfit, setColumnProfit]     = useState({});
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  // Some status queries failed: the board still shows what did load, with a
  // retry banner, and the result is NOT cached. `error` above hides the whole
  // board, which is too much for one failed status.
  const [loadFailed, setLoadFailed]         = useState(false);
  const [activeId, setActiveId]             = useState(null);
  const [selectMode, setSelectMode]         = useState(false);
  const [selectedIds, setSelectedIds]       = useState(() => new Set());
  const [isHiding, setIsHiding]             = useState(false);
  const [bulkMessage, setBulkMessage]       = useState(null);
  const [saleLeadItem, setSaleLeadItem]     = useState(null);
  const [dealDateItem, setDealDateItem]     = useState(null);
  const [moveItem, setMoveItem]             = useState(null);

  const boardRef              = useRef(null);
  // Module-scoped so it outlives this component (see boardCache above).
  const cacheRef              = useRef(boardCache);
  const fetchGenRef           = useRef(0);
  // Per-column, per-status page cursors: { [colId]: { [status]: { loaded, hasMore } } }.
  // The `new` column is three status queries, so one shared offset over the
  // merged list skipped and duplicated rows on load-more.
  const pagingRef             = useRef({});

  // MouseSensor (desktop) + TouchSensor (mobile). Avoid PointerSensor alongside
  // TouchSensor — both fire on touch devices and fight carousel swipe vs drag.
  // Touch delay lets users swipe cards horizontally; long-press then drag to move status.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 10 } })
  );

  // Scoped to the viewer + active store so a cached board can never be shown
  // to a different user or after a store switch.
  const viewKey = scopedCacheKey(user?.id, activeStoreId, `${requestTab}|${admin}|${search}|${period}`);

  const doFetch = useCallback(async (cacheKey, tab, adm, srch, per, userId) => {
    const gen = ++fetchGenRef.current;
    const endpoint = tab === "sell" ? "sell" : "order";

    // Never blank the board: the cards already on screen stay put and each
    // column swaps in place as its queries land. Emptying `items` here is what
    // made every filter change — and every ORDER/SELL flip with a search
    // active — flash an empty board.
    setError(null);
    setLoadFailed(false);
    setColumnLoading(Object.fromEntries(COLUMNS.map((c) => [c.id, true])));
    setLoading(false);
    let anyStatusFailed = false;

    const accItems = {};
    const accTotals = {};
    const accProfits = {};
    const accPaging = {};

    await Promise.all(
      COLUMNS.map(async (col) => {
        const colId = col.id;
        const colItems = [];
        const colPaging = {};
        let colTotal = 0;
        let colProfit = null;

        await Promise.all(
          COLUMN_STATUSES[colId].map(async (status) => {
            try {
              // A 5xx resolves undefined (global interceptor) — make it a
              // failure so the column is not shown as a real, empty result.
              const { data } = requireResponse(await axios.post(`/api/user/${userId}/requests/${endpoint}`, {
                filters: { status, channel: "", admin: adm, showDeleted: false, search: srch, owner: "dao", period: PERIOD_MAP[per] },
                sort: { field: "created_at", direction: "desc" },
                paging: { offset: 0, limit: PAGE_SIZE },
              }));
              const statusItems = data.items || [];
              colItems.push(...statusItems);
              colPaging[status] = { loaded: statusItems.length, hasMore: statusItems.length === PAGE_SIZE };
              colTotal += data.total || 0;
              // Server gross-profit aggregate (only returned for order "done").
              if (data.profit !== null && data.profit !== undefined) {
                colProfit = (colProfit ?? 0) + data.profit;
              }
            } catch {
              // A failed status must not advertise a page we can't fetch.
              colPaging[status] = { loaded: 0, hasMore: false };
              anyStatusFailed = true;
            }
          })
        );

        if (fetchGenRef.current !== gen) return;

        accItems[colId] = colItems;
        accTotals[colId] = colTotal;
        accProfits[colId] = colProfit;
        accPaging[colId] = colPaging;
        pagingRef.current[colId] = { ...colPaging };

        setItems((prev) => [
          ...prev.filter((i) => getItemColumn(i) !== colId),
          ...colItems,
        ]);
        setColumnHasMore((p) => ({ ...p, [colId]: hasMoreFromPaging(colPaging) }));
        setColumnTotal((p) => ({ ...p, [colId]: colTotal }));
        setColumnProfit((p) => ({ ...p, [colId]: colProfit }));
        setColumnLoading((p) => ({ ...p, [colId]: false }));
      })
    );

    if (fetchGenRef.current !== gen) return;

    // Never cache a partial board — it would re-serve the failure (empty
    // columns, wrong totals) as real data for the whole cache lifetime.
    if (anyStatusFailed) {
      setLoadFailed(true);
      return;
    }

    writeCache(cacheRef.current, cacheKey, {
      fetchedAt: Date.now(),
      items: COLUMNS.flatMap((c) => accItems[c.id] || []),
      totals: accTotals,
      profits: accProfits,
      paging: accPaging,
    });
  }, []);

  const refetch = useCallback(() => {
    if (!user?.id) return;
    cacheRef.current.delete(viewKey);
    doFetch(viewKey, requestTab, admin, search, period, user.id);
  }, [doFetch, viewKey, requestTab, admin, search, period, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    // A cache entry inside its TTL is served without any request at all — that
    // is what makes an ORDER↔SELL flip, or coming back to the board, free
    // instead of another 8 POSTs. Past the TTL the entry is dropped rather than
    // shown-then-silently-replaced, so cards never mutate without a signal.
    const cached = readCache(cacheRef.current, viewKey);
    if (cached) {
      setItems(cached.items);
      setColumnTotal(cached.totals);
      setColumnProfit(cached.profits || {});
      pagingRef.current = clonePaging(cached.paging);
      setColumnHasMore(
        Object.fromEntries(COLUMNS.map((c) => [c.id, hasMoreFromPaging(cached.paging?.[c.id])]))
      );
      setColumnLoading({});
      setLoading(false);
      setError(null);
      // Instant board, then bring it up to date. Without this a cache hit could
      // show minutes-old cards with no signal; with it the rep sees their board
      // immediately and each column refreshes under its own spinner.
      if (Date.now() - cached.fetchedAt > STALE_AFTER_MS) {
        doFetch(viewKey, requestTab, admin, search, period, user.id);
      }
      return;
    }
    doFetch(viewKey, requestTab, admin, search, period, user.id);
  }, [doFetch, viewKey, requestTab, admin, search, period, user?.id]);

  // One page per status, each with its own cursor. The `new` column is three
  // status queries merged into one list, so a single offset over that list both
  // skipped rows and re-fetched rows already shown.
  const loadMoreForColumn = useCallback(async (colId) => {
    if (columnLoading[colId] || !columnHasMore[colId]) return;
    const paging = pagingRef.current[colId] || {};
    const statuses = COLUMN_STATUSES[colId].filter((s) => paging[s]?.hasMore);
    if (statuses.length === 0) {
      setColumnHasMore((p) => ({ ...p, [colId]: false }));
      return;
    }

    setColumnLoading((p) => ({ ...p, [colId]: true }));
    const endpoint = requestTab === "sell" ? "sell" : "order";
    const nextPaging = { ...paging };
    const fetched = [];

    await Promise.all(
      statuses.map(async (status) => {
        const cursor = paging[status];
        try {
          const { data } = requireResponse(await axios.post(`/api/user/${user.id}/requests/${endpoint}`, {
            filters: { status, channel: "", admin, showDeleted: false, search, owner: "dao", period: PERIOD_MAP[period] },
            sort: { field: "created_at", direction: "desc" },
            paging: { offset: cursor.loaded, limit: PAGE_SIZE },
          }));
          const statusItems = data.items || [];
          fetched.push(...statusItems);
          nextPaging[status] = {
            loaded: cursor.loaded + statusItems.length,
            hasMore: statusItems.length === PAGE_SIZE,
          };
        } catch {
          // Keep the cursor so "load more" can be retried, instead of the
          // column silently looking complete.
          nextPaging[status] = { ...cursor };
          setLoadFailed(true);
        }
      })
    );

    pagingRef.current[colId] = nextPaging;
    if (fetched.length > 0) {
      // Guard against a row arriving twice (a card moved between pages
      // server-side) — the board keys on item id.
      setItems((p) => {
        const seen = new Set(p.map((i) => i.id));
        return [...p, ...fetched.filter((i) => !seen.has(i.id))];
      });
    }
    setColumnHasMore((p) => ({ ...p, [colId]: hasMoreFromPaging(nextPaging) }));
    setColumnLoading((p) => ({ ...p, [colId]: false }));

    // Keep the snapshot in step with what the board now shows, so returning to
    // this filter restores every page the user had loaded, not just the first.
    const cached = cacheRef.current.get(viewKey);
    if (cached) {
      const seen = new Set(cached.items.map((i) => i.id));
      writeCache(cacheRef.current, viewKey, {
        ...cached,
        items: [...cached.items, ...fetched.filter((i) => !seen.has(i.id))],
        paging: { ...cached.paging, [colId]: clonePaging({ [colId]: nextPaging })[colId] },
      });
    }
  }, [columnLoading, columnHasMore, viewKey, requestTab, admin, search, period, user?.id]);

  const getColumnItems = (colId) => {
    let col = items.filter((i) => getItemColumn(i) === colId).sort((a, b) => b.id - a.id);
    if (showStaleOnly) col = col.filter((i) => getStaleType(i) !== null);
    return col;
  };
  const activeItem = activeId ? items.find((i) => String(i.id) === activeId) : null;

  const handleDragStart = ({ active }) => setActiveId(String(active.id));
  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const itemId = Number(active.id);
    const targetColumn = over.id;
    const item = items.find((i) => i.id === itemId);
    if (!item || getItemColumn(item) === targetColumn) return;
    setItems((p) => p.map((i) => (i.id === itemId ? { ...i, status: targetColumn } : i)));
    // The cached board now outlives this mount, so a card moved here would come
    // back in its old column on the next visit unless the cache is dropped.
    try {
      // requireResponse: a 5xx resolves undefined — without it the card stayed
      // in its new column as if the move had saved.
      requireResponse(await axios.put(`/api/requests/${itemId}/admin`, { status: targetColumn }));
      cacheRef.current.clear();
    } catch {
      setItems((p) => p.map((i) => (i.id === itemId ? { ...i, status: item.status } : i)));
    }
  };
  const handleDragCancel = () => setActiveId(null);

  const handleTabChange = (tab) => { setView({ requestTab: tab }, true); };

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const enterSelectMode = useCallback(() => {
    setSelectMode(true);
    setBulkMessage(null);
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkMessage(null);
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Bulk Cancel & Hide: mirror the per-card flow in
  // src/order/components/SellItem/AdminActions.jsx — two sequential PUTs per
  // request (status: "canceled" then hidden: true). Optimistically remove
  // affected cards; restore any that fail and surface a count.
  const handleBulkHide = useCallback(async () => {
    if (selectedIds.size === 0 || isHiding) return;
    const ids = Array.from(selectedIds);
    const idsSet = new Set(ids);
    const itemsToHide = items.filter((i) => idsSet.has(i.id));

    setIsHiding(true);
    setBulkMessage(null);
    setItems((p) => p.filter((i) => !idsSet.has(i.id)));
    setColumnTotal((p) => {
      const next = { ...p };
      itemsToHide.forEach((it) => {
        const col = getItemColumn(it);
        next[col] = Math.max(0, (next[col] || 0) - 1);
      });
      return next;
    });

    const results = await Promise.allSettled(
      ids.map(async (id) => {
        requireResponse(await axios.put(`/api/requests/${id}/admin`, { status: "canceled" }));
        requireResponse(await axios.put(`/api/requests/${id}/admin`, { hidden: true }));
        return id;
      })
    );

    const failedIds = results
      .map((r, i) => (r.status === "rejected" ? ids[i] : null))
      .filter((x) => x !== null);

    if (failedIds.length > 0) {
      const failedSet = new Set(failedIds);
      const failedItems = itemsToHide.filter((i) => failedSet.has(i.id));
      // Restore without doubling a card a column refetch already brought back.
      setItems((p) => {
        const seen = new Set(p.map((i) => i.id));
        return [...p, ...failedItems.filter((i) => !seen.has(i.id))];
      });
      setColumnTotal((p) => {
        const next = { ...p };
        failedItems.forEach((it) => {
          const col = getItemColumn(it);
          next[col] = (next[col] || 0) + 1;
        });
        return next;
      });
      setBulkMessage(`Hidden ${ids.length - failedIds.length} of ${ids.length}. ${failedIds.length} failed.`);
    } else {
      setBulkMessage(`Hidden ${ids.length} request${ids.length === 1 ? "" : "s"}.`);
    }

    cacheRef.current.clear();
    setIsHiding(false);
    setSelectMode(false);
    setSelectedIds(new Set());
    setTimeout(() => setBulkMessage(null), 4000);
  }, [selectedIds, isHiding, items]);

  // Per-card "⋯" menu actions — mirror the Orders-tab AdminActions flow, but
  // reuse this board's optimistic state model instead of a full page reload.
  const moveToInProgress = useCallback(async (item) => {
    if (getItemColumn(item) === "in_progress") return;
    setItems((p) => p.map((i) => (i.id === item.id ? { ...i, status: "in_progress" } : i)));
    try { requireResponse(await axios.put(`/api/requests/${item.id}/admin`, { status: "in_progress" })); }
    catch { setItems((p) => p.map((i) => (i.id === item.id ? { ...i, status: item.status } : i))); }
  }, []);

  // Single-card Cancel & Hide: same two sequential PUTs as handleBulkHide
  // (status: "canceled" then hidden: true). Optimistically drop the card and
  // restore it if either call fails.
  const hideSingle = useCallback(async (item) => {
    const col = getItemColumn(item);
    setItems((p) => p.filter((i) => i.id !== item.id));
    setColumnTotal((p) => ({ ...p, [col]: Math.max(0, (p[col] || 0) - 1) }));
    try {
      requireResponse(await axios.put(`/api/requests/${item.id}/admin`, { status: "canceled" }));
      requireResponse(await axios.put(`/api/requests/${item.id}/admin`, { hidden: true }));
      cacheRef.current.clear();
    } catch {
      setItems((p) => (p.some((i) => i.id === item.id) ? p : [...p, item]));
      setColumnTotal((p) => ({ ...p, [col]: (p[col] || 0) + 1 }));
      setBulkMessage("Failed to hide request.");
      setTimeout(() => setBulkMessage(null), 4000);
    }
  }, []);

  const handleCardAction = useCallback((item, action) => {
    if (action === "broadcast") {
      if (item.broadcastWhatsappMessage) {
        // The store's own template when it has one (Owner Admin → Message templates).
        storeCaption(
          "request",
          item.id,
          item.broadcastWhatsappMessage.replace(/\\n/g, "\n").replace(/__SQR__/g, "▪️")
        ).then((message) => ShareHelper.whatsapp(message));
      }
      return;
    }
    if (action === "copy_image") {
      // Not awaited: shareImage has to reach clipboard.write() inside the click's
      // user activation, and it reports its own outcome through the alerts.
      shareImage(item, {
        onInfo: (message) => alert.info(message),
        onError: (message) => alert.error(message),
      });
      return;
    }
    if (action === "in_progress") return moveToInProgress(item);
    if (action === "canceled") return hideSingle(item);
    if (action === "change_lead") return setSaleLeadItem(item);
    if (action === "change_date") return setDealDateItem(item);
    if (action === "move_transaction") return setMoveItem(item);
  }, [moveToInProgress, hideSingle, alert]);

  // Reassign the deal's sale lead, then drop the cache and refetch so the
  // owner/commission avatars (and downstream P&L) reflect the new lead.
  const confirmSaleLead = async (userId) => {
    if (!saleLeadItem) return;
    // Throws on a swallowed 5xx so ChangeSaleLeadModal shows its error and
    // stays open, instead of closing as if the lead had changed.
    requireResponse(await axios.put(`/api/requests/${saleLeadItem.id}/sale-lead`, { user_id: userId }));
    cacheRef.current.clear();
    setSaleLeadItem(null);
    refetch();
  };

  const grandTotal = Object.values(columnTotal).reduce((s, v) => s + (v || 0), 0);

  return (
    // No page-specific shell class. `.kanban-page-shell` capped the page at
    // `100dvh` with `overflow: hidden` and made it a flex column — wrapped around
    // PageHeader's sticky positioning, that is what broke the title and the
    // description. The page scrolls as a document, like every other route.
    <PageLayout>
      <PageHeader
        title="Kanban"
        description="Every open order and sell request, by stage."
        backTo={HOME_PATH}
      />

      <main id="pages" className={`kanban-page kanban-page--bp-${bp}${isMobile ? " kanban-page--mobile" : ""}`}>
        <div className="kanban-controls">
          {/* Controlled, not routed: these switch state, not the URL. */}
          {/* `underline`, like Manage Listings: the active label stays white with
              an ink bar under it. The `pill` variant colours it with the accent,
              which is what made the selected tab read purple here. */}
          <Tabs
            variant="underline"
            aria-label="Request type"
            value={requestTab}
            onChange={(next) => handleTabChange(next)}
            items={[
              { value: "order", label: `Order request${requestTab === "order" && grandTotal > 0 ? ` (${grandTotal})` : ""}` },
              { value: "sell", label: `Sell request${requestTab === "sell" && grandTotal > 0 ? ` (${grandTotal})` : ""}` },
            ]}
          />

          <div className="kanban-controls__right">
            <Segmented
              size="md"
              label="Period"
              value={period}
              onChange={setPeriod}
              options={PERIODS.map((p) => ({ value: p.key, label: p.label }))}
            />

            <KanbanUserFilter admin={admin} setAdmin={setAdmin} />

            <DebounceInput
              element={SearchInput}
              debounceTimeout={300}
              size="md"
              auto
              className="kanban-search"
              name="search"
              inputMode="search"
              enterKeyHint="search"
              placeholder="Search requests"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch("")}
            />

            <Button
              type="button"
              variant={showStaleOnly ? "secondary" : "ghost"}
              size="md"
              onClick={toggleStaleOnly}
            >
              Stale
            </Button>

            <Button
              type="button"
              variant={selectMode ? "secondary" : "ghost"}
              size="md"
              disabled={isHiding}
              onClick={selectMode ? exitSelectMode : enterSelectMode}
            >
              {selectMode ? "Exit" : "Cancel & hide"}
            </Button>
          </div>
        </div>

        {error && <div className="kanban-error">{error}</div>}
        {loadFailed && !error && (
          <div className="kanban-error" role="alert">
            Some requests couldn&rsquo;t load, so columns may be incomplete.{" "}
            <Button variant="secondary" size="sm" onClick={refetch}>Try again</Button>
          </div>
        )}

        {/* F5: a brand-new store sees one friendly line above the (still drawn) board.
            Only on the default view — any user / search / period / Stale pick means the
            board is empty because of the filter, and the per-column "No requests" says so. */}
        {!loading && !error && !loadFailed && items.length === 0 && isDefaultView && (
          <StatePanel
            state="empty"
            title="Your board is empty"
            description="Requests move across these columns as you work them."
            action={{ label: "New sourcing enquiry", to: "/request/sourcing", ...storeGuard }}
          />
        )}

        {!loading && !error && (
          <>
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
              <div ref={boardRef} className={`kanban-board kanban-board--bp-${bp}`}>
                {COLUMNS.map((col) => (
                  <KanbanColumn key={col.id} column={col} items={getColumnItems(col.id)}
                    total={columnTotal[col.id] ?? null}
                    isSell={requestTab === "sell"}
                    settings={settings}
                    hasMore={!!columnHasMore[col.id]} isLoadingMore={!!columnLoading[col.id]}
                    onLoadMore={() => loadMoreForColumn(col.id)}
                    selectMode={selectMode}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onCardAction={handleCardAction}
                    serverProfit={columnProfit[col.id] ?? null}
                    period={period}
                    layout={layout} />
                ))}
              </div>
              <DragOverlay dropAnimation={null}>
                {activeItem && (
                  <div className={`kanban-card kanban-card--ghost${layout.compactCards ? " kanban-card--compact" : ""}`}>
                    <CardContent item={activeItem} settings={settings} compact={layout.compactCards} />
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </>
        )}

        {selectMode && selectedIds.size > 0 && (
          <div className="kanban-bulk-bar" role="toolbar" aria-label="Bulk actions">
            <span className="kanban-bulk-bar__count">{selectedIds.size} selected</span>
            <button
              type="button"
              className="kanban-bulk-bar__action"
              onClick={handleBulkHide}
              disabled={isHiding}
            >
              {isHiding ? "Hiding…" : "Cancel & Hide"}
            </button>
            <button
              type="button"
              className="kanban-bulk-bar__clear"
              onClick={clearSelection}
              disabled={isHiding}
            >
              Clear
            </button>
          </div>
        )}

        {bulkMessage && <div className="kanban-bulk-message">{bulkMessage}</div>}
      </main>
      {saleLeadItem && (
        <ChangeSaleLeadModal
          onClose={() => setSaleLeadItem(null)}
          onConfirm={confirmSaleLead}
        />
      )}
      {dealDateItem && (
        <ChangeDealDateModal
          currentDate={dealDateItem.created_at}
          onClose={() => {
            setDealDateItem(null);
            // Always refetch on close: the card has almost certainly moved OUT
            // of the current period filter (that is the point of the action), and
            // a change that succeeded in RC but failed in Xero keeps the modal
            // open on a warning — so "closed" is not the same as "nothing
            // changed". Cache is dropped for the same reason confirmSaleLead
            // drops it: the board's per-filter cache would re-serve the old row.
            cacheRef.current.clear();
            refetch();
          }}
          onConfirm={async (date) => {
            const { data } = await axios.put(
              `/api/requests/${dealDateItem.id}/deal-date`,
              { date },
              { metadata: { silent: true } }
            );
            return data;
          }}
        />
      )}
      {moveItem && (
        <MoveTransactionModal
          requestId={moveItem.id}
          onClose={() => {
            setMoveItem(null);
            // Same reasoning as the deal-date modal: the card has almost
            // certainly left this board (it now belongs to another store), and a
            // move whose Xero void failed keeps the modal open on a warning — so
            // "closed" is not "nothing changed". The per-filter cache has to be
            // dropped or the board re-serves the moved row.
            cacheRef.current.clear();
            refetch();
          }}
          onMoved={(storeName) =>
            alert.show(`Moved to ${storeName}. Switch store to view it.`, {
              type: "success",
            })
          }
        />
      )}
    </PageLayout>
  );
};

export default KanbanRequests;
